import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, ListTodo, FileText, PenLine } from 'lucide-react';
import { buildApiUrl } from '@/utils/apiClient';
import { toast } from 'react-toastify';

interface NoteNextStepsProps {
  noteId: string;
  status: string;
  hasBatchNotes: boolean;
  currentNoteIndex: number;
  batchNotesLength: number;
  onNavigateNext: () => void;
  onWriteAnother: () => void;
}

/**
 * Post-signing "Next Steps" panel shown after a note is signed/co-signed/incorporated.
 * Extracted from NotePreview to keep file sizes manageable.
 */
function NoteNextSteps({
  noteId,
  status,
  hasBatchNotes,
  currentNoteIndex,
  batchNotesLength,
  onNavigateNext,
  onWriteAnother,
}: NoteNextStepsProps) {
  const navigate = useNavigate();

  const statusLabel =
    status === 'signed'
      ? 'Signed'
      : status === 'co_signed'
        ? 'Co-Signed'
        : status === 'incorporated'
          ? 'Incorporated'
          : status;

  const isIncorporated = status === 'incorporated';
  const wrapperClass = isIncorporated
    ? 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl p-4'
    : 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-xl p-4';
  const iconClass = isIncorporated
    ? 'w-5 h-5 text-gray-500 dark:text-gray-400'
    : 'w-5 h-5 text-green-600 dark:text-green-400';
  const titleClass = isIncorporated
    ? 'font-medium text-gray-800 dark:text-gray-200'
    : 'font-medium text-green-800 dark:text-green-200';
  const textClass = isIncorporated
    ? 'text-sm text-gray-700 dark:text-gray-300 mb-4'
    : 'text-sm text-green-700 dark:text-green-300 mb-4';

  return (
    <div className="mt-4 flex-shrink-0">
      <div className={wrapperClass}>
        <div className="flex items-center gap-2 mb-3">
          <Check className={iconClass} />
          <span className={titleClass}>
            Note {statusLabel}
          </span>
        </div>
        <p className={textClass}>
          What would you like to do next?
        </p>
        <div className="flex flex-wrap gap-3">
          {hasBatchNotes && currentNoteIndex < batchNotesLength - 1 && (
            <button
              onClick={onNavigateNext}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
            >
              <ArrowRight className="w-4 h-4" />
              Write Next Note
            </button>
          )}
          <button
            onClick={onWriteAnother}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium"
          >
            <PenLine className="w-4 h-4" />
            Write Another Note
          </button>
          <button
            onClick={() => navigate('/todo')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition font-medium"
          >
            <ListTodo className="w-4 h-4" />
            Return to Tasks
          </button>
          <button
            onClick={() => navigate('/notes')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition font-medium"
          >
            <FileText className="w-4 h-4" />
            View All Notes
          </button>
          <button
            onClick={() => {
              const format = 'pdf';
              window.open(buildApiUrl(`/notes/${noteId}/export/${format}`), '_blank');
              toast.success(`Note exported as ${format.toUpperCase()} successfully!`);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition font-medium"
          >
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}

export default NoteNextSteps;
